import React, { useState } from 'react';
import useExtraSmallScreen from 'hooks/useExtraSmallScreen';
import Divider from 'housecall-ui/dist/DesignSystem/DataDisplay/Divider/Divider';
import List from 'housecall-ui/dist/DesignSystem/DataDisplay/List/List';
import ListItem from 'housecall-ui/dist/DesignSystem/DataDisplay/List/ListItem';
import Tooltip from 'housecall-ui/dist/DesignSystem/DataDisplay/Tooltips/Tooltip';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import IconButton from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/IconButton';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import Drawer from 'housecall-ui/dist/DesignSystem/Navigation/Drawers/Drawer';
import DrawerHeader from 'housecall-ui/dist/DesignSystem/Navigation/Drawers/DrawerHeader';
import Tabs from 'housecall-ui/dist/DesignSystem/Navigation/Tabs/Tabs';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import CloseIcon from 'housecall-ui/dist/IconsAndImages/Icons/Close';

import BrowserPush from '@/BrowserPush';
import RedirectTo from 'components/Common/RedirectTo';
import ProAvatar from 'components/ProAvatar.js';
import ActivityFeed from 'components/Toolbar/ActivityFeed/ActivityFeed.js';
import url from 'components/url';

import { fetchPro } from '../../../../Resources/Pro/ProData';
import { useDataFetcherV2 } from '../../../Common/Data/useDataFetcher';

import TabLink from './TabLink';

const useStyles = makeStyles({
  avatarAccount: {
    paddingLeft: 16,
    paddingBottom: 16,
  },
  infoAccount: {
    maxWidth: 232,
  },
});

type MenuTabs = {
  value: string;
  label: string;
  path: string;
  hidden?: boolean;
}[];

const AvatarMenu = () => {
  const [, pro] = useDataFetcherV2(fetchPro);

  const classes = useStyles();
  const [isAvatarDrawerOpen, setIsAvatarDrawerOpen] = useState(false);
  const [activityFeedDrawerOpen, setActivityFeedDrawerOpen] = useState(false);
  const toggleActivityFeedDrawer = () => setActivityFeedDrawerOpen(!activityFeedDrawerOpen);
  const signOut = async () => {
    await BrowserPush.disconnect();
    window.location.href = url.logout.path;
  };
  const xsScreen = useExtraSmallScreen();
  const menuTabs: MenuTabs = [
    { value: 'my_account', label: 'My account', path: url.myAccount.path },
    { value: 'settings', label: 'Settings', path: url.settings.path, hidden: !xsScreen },
  ];

  const activeTab = menuTabs.find((nav) => window.location.pathname.includes(nav.path));

  return (
    <>
      <IconButton edge="end" version="v2" aria-label="Account Menu" onClick={() => setIsAvatarDrawerOpen(true)}>
        <ProAvatar
          backgroundColor={`#${pro.color_hex}`}
          avatar={pro.avatar_url}
          has_avatar={pro.has_avatar}
          initials={pro.initials}
          useLatestMaterialUI
        />
      </IconButton>
      <Drawer
        open={isAvatarDrawerOpen}
        onClose={() => setIsAvatarDrawerOpen(false)}
        version="v2"
        anchor="right"
        style={{ overflow: 'hidden' }}
      >
        <DrawerHeader>
          <Grid container justify="flex-end">
            <IconButton onClick={() => setIsAvatarDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </DrawerHeader>
        <Grid container spacing={2} className={classes.avatarAccount}>
          <Grid item>
            <ProAvatar
              backgroundColor={`#${pro.color_hex}`}
              avatar={pro.avatar_url}
              has_avatar={pro.has_avatar}
              initials={pro.initials}
              useLatestMaterialUI
              size={48}
            />
          </Grid>
          <Grid item className={classes.infoAccount}>
            <Typography variant="body1" noWrap>
              {pro.full_name}
            </Typography>
            <Tooltip title={pro.email}>
              <Typography variant="body1" noWrap>
                {pro.email}
              </Typography>
            </Tooltip>
          </Grid>
        </Grid>
        <Divider />
        <Box pb={1}>
          <Tabs
            orientation="vertical"
            tabSize="med"
            onChange={() => null}
            value={activeTab?.value || false}
            version="v2"
            width="full"
          >
            {menuTabs.map(({ value, label, path, hidden }, _index) => {
              if (hidden) return null;
              return (
                <TabLink
                  key={path}
                  version="v2"
                  href={path}
                  label={<Typography variant="h3">{label}</Typography>}
                  tabIndex={0}
                  value={value}
                />
              );
            })}
          </Tabs>
        </Box>
        <Divider />
        <List component="nav">
          <ListItem alignItems="center" button component="a" onClick={toggleActivityFeedDrawer}>
            <Typography variant="h3" color="textPrimary">
              Activity feed
            </Typography>
          </ListItem>
          <ListItem alignItems="center" button component="a" onClick={() => RedirectTo.helpCenter()}>
            <Typography variant="h3" color="textPrimary">
              Help
            </Typography>
          </ListItem>
          <ListItem alignItems="center" button component="a" onClick={signOut}>
            <Typography variant="h3" color="textPrimary">
              Sign out
            </Typography>
          </ListItem>
        </List>
      </Drawer>
      <ActivityFeed
        isActivityFeedDrawerOpen={activityFeedDrawerOpen}
        toggleActivityFeedDrawer={toggleActivityFeedDrawer}
      />
    </>
  );
};

export default AvatarMenu;
